<script setup>
// Attendee/member rail rows (mockups/redesign/meet-event.html .att-list):
// avatar + name (+ optional waitlist position / trailing chip), my row tinted.
// Used for the hub Members rail and the event page Going / Waitlist /
// Can't-make-it rails. Rows accept MeetMemberResponse or MeetAttendanceResponse
// shapes (both carry playerId / playerName / playerImageFileName).
import AvatarImageLoader from '~/components/loaders/AvatarImageLoader.vue';

defineProps({
  rows: {type: Array, default: () => []},
  myPlayerId: {type: String, default: null},
  // Waitlist rails show "#1 / #2" prefixes
  showPositions: {type: Boolean, default: false},
  // Declined rail — low-key rows
  muted: {type: Boolean, default: false},
});

const initials = (name) => (name || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
</script>

<template>
  <div class="tl-card att-list" :class="{'att-list--muted': muted}">
    <router-link
        v-for="(row, i) in rows"
        :key="row.id ?? row.playerId ?? i"
        :to="{name: 'PlayerDetail', params: {playerId: row.playerId}}"
        class="att-row"
        :class="{'att-row--me': myPlayerId && row.playerId === myPlayerId}"
    >
      <span v-if="showPositions" class="att-row__wl">#{{ row.waitlistPosition ?? i + 1 }}</span>
      <avatar-image-loader
          v-if="row.playerImageFileName"
          :image-file-name="row.playerImageFileName"
          :image-clickable="false"
          size="30"
          class="att-row__face"
      />
      <div v-else class="att-row__face att-row__face--fallback">{{ initials(row.playerName) }}</div>
      <span class="att-row__name">{{ row.playerName }}</span>
      <slot name="trailing" :row="row"/>
    </router-link>
    <div v-if="rows.length === 0" class="att-row att-row--empty">
      <slot name="empty">No one yet</slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.att-list {
  display: flex;
  flex-direction: column;
  max-height: 420px;
  overflow-y: auto;

  &--muted .att-row {
    font-weight: 500;
    color: rgb(var(--v-theme-textSubtle));

    .att-row__face {
      opacity: 0.55;
    }
  }
}

.att-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  color: rgb(var(--v-theme-text));

  & + & {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &--me {
    background: rgb(var(--v-theme-brandTint));
  }

  &--empty {
    justify-content: center;
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
    cursor: default;
  }

  &__wl {
    font-size: 10.5px;
    font-weight: 700;
    color: rgb(var(--v-theme-textSubtle));
    width: 22px;
    flex-shrink: 0;
  }

  &__face {
    width: 30px !important;
    height: 30px !important;
    border-radius: 50% !important;
    flex-shrink: 0;

    &--fallback {
      background: rgb(var(--v-theme-light));
      border: 1px solid rgb(var(--v-theme-borderLight));
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      font-weight: 700;
      color: rgb(var(--v-theme-charcoal));
    }
  }

  &__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
